/**
 * Today's sky and temperature in the header, as a button that opens the
 * Weather page.
 *
 * Weather used to be one of the four tabs along the bottom. Seasons took
 * that slot, and the weather moved up here, where it is on every tab rather
 * than only in the block at the top of Plants. It is drawn as a pill with a
 * border, a fill and a chevron so it reads as something to tap, not a
 * reading; the avatar beside it is the same size.
 *
 * On the Weather page the pill is selected: filled in the theme's "where you
 * are" colour (active), with aria-current="page" (NavLink).
 *
 * The glyph is the server's (W24 Job D): the header answer's pillIcon, a sky
 * key drawn by SkyIcon. The server makes it the snowflake under a frost or a
 * freeze tonight, whatever the sky, because frost is the one reading a
 * gardener must not miss (B92: a night only too cold for a tender crop, "Too
 * cold for the ginger tonight, 35°F", is no frost and keeps the sky), else the
 * sky's own key (sun or moon, clouds, rain, snow). The app has no rule of its
 * own, so the pill and the frost bar, both the header answer's, agree.
 *
 * The temperature and the sky's words (the pill's title) are the ones every
 * page shows (useWeatherNow), so the header and Today never disagree.
 *
 * Nothing until the reading has arrived; a header that says "--°" is noise.
 *
 * Used by: the Header only.
 * API: none of its own: the header answer (WeatherContext, GET /header),
 * through useWeatherNow and useWeatherContext.
 * Words (en/es): nav.weather.
 *
 * @module WeatherPill
 */

import { NavLink } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useWeatherNow } from '@/shared/hooks/useWeatherNow';
import { useWeatherContext } from '@/shared/hooks/useWeatherContext';
import { SkyIcon } from '@/shared/components/SkyIcon';

/**
 * The header's weather: today's sky and temperature, linking to Weather.
 *
 * @param className - Extra classes from where it sits.
 * @returns The pill, or nothing until the reading has arrived.
 */
export function WeatherPill({ className }: { className?: string }): JSX.Element | null {
  const { reactTranslate: t } = useTranslation();
  const now = useWeatherNow();
  // The icon the server says the pill draws (the snowflake under a frost tonight, else the sky's).
  const { pillIcon } = useWeatherContext();

  if (!now?.display) return null;

  return (
    <NavLink
      to="/weather"
      aria-label={t('nav.weather')}
      title={now.words}
      className={({ isActive }) => cn(
        'inline-flex h-8 items-center gap-1 rounded-full border pl-2 pr-1.5 text-sm font-semibold shadow-sm transition-colors',
        isActive
          // On the Weather page: selected, filled in the theme's "where you are" colour.
          ? 'border-active bg-active text-active-foreground'
          : 'border-border bg-card text-primary hover:bg-muted',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      <SkyIcon sky={pillIcon} className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden="true" />
      <span className="tabular-nums">{now.display}</span>
      <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden="true" />
    </NavLink>
  );
}
